import { type ReactNode } from 'react';

import { memoGeneric } from '@/js/libs/memo/index.ts';

import { isEqualOptions } from '../../helpers.ts';
import type { Option } from '../../types.ts';
import { OptionCheckbox } from '../OptionCheckbox/index.tsx';
import { OptionSelect } from '../OptionSelect/index.tsx';

interface OptionsListProps<T> {
  options: Option<T>[];
  groupName?: string;
  isMulti?: boolean;
  selected: Option<T>[] | Option<T> | null;
  focusedIndex?: number;
  getOptionKey?: (option: Option<T>) => string | number;
  optionSelected?: (selected: Option<T>, option: Option<T>) => boolean;
  formatOptionLabel?: (option: Option<T>) => ReactNode;
  handleClick: (option: Option<T>) => () => void;
}

export const OptionsList = memoGeneric(function OptionsList<T>({
  options,
  groupName,
  isMulti,
  selected,
  focusedIndex = -1,
  getOptionKey,
  optionSelected,
  formatOptionLabel,
  handleClick,
}: OptionsListProps<T>) {
  return (
    <>
      {isMulti &&
        options.map((option, i) => (
          <OptionCheckbox
            key={getOptionKey ? getOptionKey(option) : groupName ? `${groupName}-${i}` : i}
            option={option}
            checked={
              !!(selected as Option<T>[])?.some((item: Option<T>) =>
                optionSelected ? optionSelected(item, option) : isEqualOptions(item, option),
              )
            }
            isFocused={i === focusedIndex}
            onClick={handleClick(option)}
            formatOptionLabel={formatOptionLabel}
          />
        ))}
      {!isMulti &&
        options.map((option, i) => (
          <OptionSelect
            key={getOptionKey ? getOptionKey(option) : groupName ? `${groupName}-${i}` : i}
            option={option}
            selected={selected as Option<T> | null}
            isFocused={i === focusedIndex}
            onClick={handleClick(option)}
            optionSelected={optionSelected}
            formatOptionLabel={formatOptionLabel}
          />
        ))}
    </>
  );
});
